import React, { useState } from 'react';
import { useOnboarding } from './OnboardingSystem';

const OnboardingDevPanel = ({ user, onShowWelcome }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { startOnboarding, skipOnboarding, completeOnboarding, isOnboardingActive } = useOnboarding();

  // Only show in development mode
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  const getOnboardingSteps = () => [
    {
      targetId: "stats-sidebar",
      title: "Welcome to Your Workspace!",
      content: "This sidebar shows your workspace overview. Here you can track your projects, prompts, and weekly activity. Each metric has helpful tooltips - try hovering over the icons!",
      position: "right"
    },
    {
      targetId: "block-canvas",
      title: "Your Dashboard Canvas",
      content: "This is your main workspace made of moveable blocks. You can drag them around, resize them, or add new ones. Press '/' anywhere to add a new block - try it!",
      position: "top"
    },
    {
      targetId: "help-hint",
      title: "Quick Help",
      content: "Need help anytime? This hint shows you how to add blocks, and you'll find help icons (?) throughout the interface for contextual assistance.",
      position: "top"
    },
    {
      targetId: "block-canvas",
      title: "Create Your First Project",
      content: "Ready to get started? Click on 'Create Project' to create your first project. Projects help you organize prompts by use case, application, or team.",
      position: "top"
    },
    {
      targetId: "block-canvas",
      title: "Add a Composer Block",
      content: "Once you have a project, press '/' and add a Composer block to start building visual prompt workflows. You can drag and drop different nodes to create complex AI pipelines.",
      position: "top"
    },
    {
      targetId: "stats-sidebar",
      title: "You're All Set! 🎉",
      content: "You now know the basics of Banyan! Explore the interface, create projects, and start building amazing prompts. Remember: hover for tooltips, click '?' for help, and press '/' to add blocks.",
      position: "right"
    }
  ];

  const handleStartTour = () => {
    const steps = getOnboardingSteps();
    startOnboarding(steps);
  };

  const handleShowWelcome = () => {
    if (onShowWelcome) {
      onShowWelcome();
    }
  };

  const handleResetOnboarding = () => {
    if (user?.id) {
      localStorage.removeItem(`banyan_welcome_seen_${user.id}`);
      localStorage.removeItem(`banyan_onboarding_completed_${user.id}`);
      
      // Also stop any active onboarding
      if (isOnboardingActive) {
        skipOnboarding();
      }
      
      alert('Onboarding state reset! Refresh the page to see the welcome modal.');
    }
  };

  const handleStopTour = () => {
    if (isOnboardingActive) {
      skipOnboarding();
    }
  };

  const getOnboardingStatus = () => {
    if (!user?.id) return 'No user';
    
    const hasSeenWelcome = localStorage.getItem(`banyan_welcome_seen_${user.id}`);
    const hasCompletedOnboarding = localStorage.getItem(`banyan_onboarding_completed_${user.id}`);
    
    return {
      welcomeSeen: !!hasSeenWelcome,
      onboardingCompleted: !!hasCompletedOnboarding,
      tourActive: isOnboardingActive
    };
  };

  const status = getOnboardingStatus();

  return (
    <div className="fixed bottom-4 right-4 z-[200]">
      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-purple-600 hover:bg-purple-700 text-white p-3 rounded-full shadow-lg transition-colors mb-2"
        title="Onboarding Dev Panel"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </button>

      {/* Dev Panel */}
      {isOpen && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 p-4 w-80 mb-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              🧪 Onboarding Dev Panel
            </h3>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
            >
              ✕
            </button>
          </div>

          {/* Status Section */}
          <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Current Status</h4>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span>Welcome Seen:</span>
                <span className={status.welcomeSeen ? 'text-green-600' : 'text-red-600'}>
                  {status.welcomeSeen ? '✓' : '✗'}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Onboarding Completed:</span>
                <span className={status.onboardingCompleted ? 'text-green-600' : 'text-red-600'}>
                  {status.onboardingCompleted ? '✓' : '✗'}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Tour Active:</span>
                <span className={status.tourActive ? 'text-blue-600' : 'text-gray-500'}>
                  {status.tourActive ? '🔄' : '⏸'}
                </span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-2">
            <button
              onClick={handleShowWelcome}
              className="w-full px-3 py-2 bg-celadon-500 text-white rounded-lg hover:bg-celadon-600 transition-colors text-sm font-medium"
            >
              🎉 Show Welcome Modal
            </button>

            <button
              onClick={handleStartTour}
              disabled={isOnboardingActive}
              className="w-full px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              🗺 Start Onboarding Tour
            </button>

            {isOnboardingActive && (
              <button
                onClick={handleStopTour}
                className="w-full px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium"
              >
                🛑 Stop Current Tour
              </button>
            )}

            <button
              onClick={handleResetOnboarding}
              className="w-full px-3 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm font-medium"
            >
              🔄 Reset Onboarding State
            </button>
          </div>

          {/* Keyboard Shortcuts */}
          <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-600">
            <h5 className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Keyboard Shortcuts:</h5>
            <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
              <div><kbd className="bg-gray-200 dark:bg-gray-600 px-1 rounded">Ctrl+Shift+W</kbd> - Welcome Modal</div>
              <div><kbd className="bg-gray-200 dark:bg-gray-600 px-1 rounded">Ctrl+Shift+T</kbd> - Start Tour</div>
              <div><kbd className="bg-gray-200 dark:bg-gray-600 px-1 rounded">Ctrl+Shift+R</kbd> - Reset State</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingDevPanel; 